import { useContext, useMemo } from "react";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import MerchantContext from "../../contexts/MerchantContext";
import MainSectionSection from "../../components/MainSectionSection";
import { trpc } from "../../trpc";
import CrmConfiguration from "../../components/crm/CrmConfiguration";
import ErpConfiguration from "../../components/erp/ErpConfiguration";
import InventoryConfiguration from "../../components/inventory/InventoryConfiguration";
import PortalesConfiguration from "../../components/portales/PortalesConfiguration";
import WebhooksConfiguration from "../../components/webhooks/WebhooksConfiguration";

const IntegrationsConfigurationContainer = (): JSX.Element => {
  const { merchantId, merchantType } = useContext(MerchantContext);

  const { data: merchant, isLoading: isLoadingMerchant } = trpc.merchants.get.useQuery(
    { id: merchantId },
    { enabled: !!merchantId }
  );

  const showInventarioTab = useMemo(() => merchantType !== "development", [merchantType]);
  const showCrmTab = useMemo(() => true, []); // Always show CRM
  const showErpTab = true;
  const showPortalesTab = useMemo(() => merchantType !== "development", [merchantType]);
  const showWebhooksTab = true;

  // Determine the default tab value
  const defaultTab = useMemo(() => {
    if (showInventarioTab) return "inventario";
    if (showCrmTab) return "crm";
    if (showErpTab) return "erp";
    if (showPortalesTab) return "portales";
    return "crm";
  }, [showInventarioTab, showCrmTab, showErpTab, showPortalesTab]);

  if (!merchantId) {
    return (
      <div className="flex h-full items-center justify-center">
        <p>No se encontro el negocio.</p>
      </div>
    );
  }

  if (isLoadingMerchant) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <MainSectionSection gap="24px">
      <Tabs defaultValue={defaultTab} className="flex min-h-full flex-col">
        <TabsList className="sticky top-0 z-10 flex w-full justify-start overflow-x-auto overflow-y-hidden bg-white shadow-[0_1px_2px_rgba(0,0,0,0.1)] md:justify-center md:overflow-visible">
          {showInventarioTab && (
            <TabsTrigger value="inventario" className="flex-1 px-2 text-center md:px-4">
              Inventario
            </TabsTrigger>
          )}
          {showCrmTab && (
            <TabsTrigger value="crm" className="flex-1 px-2 text-center md:px-4">
              CRM
            </TabsTrigger>
          )}
          {showErpTab && (
            <TabsTrigger value="erp" className="flex-1 px-2 text-center md:px-4">
              ERP
            </TabsTrigger>
          )}
          {showPortalesTab && (
            <TabsTrigger value="portales" className="flex-1 px-2 text-center md:px-4">
              Portales
            </TabsTrigger>
          )}
          {showWebhooksTab && (
            <TabsTrigger value="webhooks" className="flex-1 px-2 text-center md:px-4">
              Webhooks
            </TabsTrigger>
          )}
        </TabsList>
        {showInventarioTab && (
          <TabsContent value="inventario" className="flex-1 py-4">
            <InventoryConfiguration merchant={merchant} />
          </TabsContent>
        )}
        {showCrmTab && (
          <TabsContent value="crm" className="flex-1 py-4">
            <CrmConfiguration />
          </TabsContent>
        )}
        {showErpTab && (
          <TabsContent value="erp" className="flex-1 py-4">
            <ErpConfiguration />
          </TabsContent>
        )}
        {showPortalesTab && (
          <TabsContent value="portales" className="flex-1 py-4">
            <PortalesConfiguration merchant={merchant} />
          </TabsContent>
        )}
        {showWebhooksTab && (
          <TabsContent value="webhooks" className="flex-1 py-4">
            <WebhooksConfiguration />
          </TabsContent>
        )}
      </Tabs>
    </MainSectionSection>
  );
};

export default IntegrationsConfigurationContainer;
